import { Popover } from "@mui/material";
import { type FC, type MouseEvent, useCallback, useState } from "react";

import { useFiltersScope } from "~/components/filters/FiltersScope";
import { SubscriptionGuard } from "~/feature/subscription/components/SubscriptionGuard";
import { useSubscriptionGuard } from "~/feature/subscription/components/SubscriptionGuard/useSubscriptionGuard";
import { FeatureKey } from "~/types/subscription";

import {
  getMinVolumeLabel,
  getPriceChangeLabel,
  toStoredActivityFilterValue,
} from "./activityFilter24h.utils";
import { ActivityFilter24hButton } from "./components/ActivityFilter24hButton";
import { ActivityFilter24hForm } from "./components/ActivityFilter24hForm";
import type { IActivityFilter24hValue } from "./constants";

export const ActivityFilter24h: FC = () => {
  const { filters, updateFilters } = useFiltersScope();
  const { isLocked } = useSubscriptionGuard(
    FeatureKey.TWENTY_FOUR_HOUR_ACTIVITY_FILTER
  );
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const value: IActivityFilter24hValue = {
    minVolume24h: filters.minVolume24h,
    minPriceChange24h: filters.minPriceChange24h,
    maxPriceChange24h: filters.maxPriceChange24h,
  };

  const handleOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleApply = useCallback(
    (formValue: IActivityFilter24hValue) => {
      updateFilters(toStoredActivityFilterValue(formValue));
      handleClose();
    },
    [updateFilters, handleClose]
  );

  const handleClear = useCallback(() => {
    updateFilters({
      minVolume24h: undefined,
      minPriceChange24h: undefined,
      maxPriceChange24h: undefined,
    });
  }, [updateFilters]);

  return (
    <SubscriptionGuard featureKey={FeatureKey.TWENTY_FOUR_HOUR_ACTIVITY_FILTER}>
      <ActivityFilter24hButton
        minVolumeLabel={getMinVolumeLabel(value)}
        priceChangeLabel={getPriceChangeLabel(value)}
        locked={isLocked}
        onOpen={handleOpen}
        onClear={handleClear}
      />

      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <ActivityFilter24hForm initialValue={value} onApply={handleApply} />
      </Popover>
    </SubscriptionGuard>
  );
};
